import * as React from "react";
import { observer } from "mobx-react-lite";
import { Cell2Input } from "src/components/Sheet2/components";
import { DownOutlined } from "@ant-design/icons";
import { ZUid } from "src/types/zUid";
import { Cell2 } from "src/components/Sheet2/Cell2";
import { CellInternalBox } from "src/components/Sheet2/components/CellInternalBox";
import { onError } from "src/common/onError";
import styles from "./Mc2CellPoint.module.less";
import { ZMc2TableRow } from "../../Mc2EntityTypes";
import { Mc2EntityStore } from "../../Mc2EntityStore";
import { mc2Input } from "./mc2ui";
import { Mc2PointSelectModal } from "../../Mc2PointSelect";
import { mc2SummaryStore } from "../../../mc2SummaryStore";
import { Mc2PointRow } from "../../Mc2PointSelect/Mc2PointRow";
import { updateMc2Row } from "../../apiMc2Entity";

interface PropsMc2CellPoint {
  row: ZMc2TableRow;
  store: Mc2EntityStore;
}

// Особенность в том, что ячейка может работать в двух режимах:
// Если точка выбрана из библиотеки, то редактирование выполняется выбором из таблицы в дровере.
// А если нет, то редактирование при помощи текстового поля

export const Mc2CellPoint: React.FC<PropsMc2CellPoint> = observer((props) => {
  const { row, store } = props;
  const textCell = store.mkCell("point", row, "");
  if (row.dictionaryPointId) {
    return (
      <Mc2CellPointSelect
        store={store}
        row={row}
        dictionaryPointId={row.dictionaryPointId}
        pos={textCell.pos}
        cellKey={textCell.cellKey}
      />
    );
  }
  return <Cell2Input cell={textCell} editorProps={mc2Input} />;
});

interface PropsMc2CellPointSelect {
  row: ZMc2TableRow;
  store: Mc2EntityStore;
  dictionaryPointId: ZUid;
  pos: Cell2["pos"];
  cellKey: string;
}

const Mc2CellPointSelect: React.FC<PropsMc2CellPointSelect> = observer(
  (props) => {
    const { row, store, cellKey, pos, dictionaryPointId: value } = props;
    const { sheetStore } = store;
    const { settings } = mc2SummaryStore;
    const bref = React.useRef<HTMLButtonElement>(null);
    const save = async (newValue: unknown) => {
      const res = newValue as Mc2PointRow;
      await updateMc2Row({
        ...row,
        dictionaryPointId: res.id,
      });
      return () => {
        row.dictionaryPointId = res.id;
        row.point = res.name;
        row.description = res.description;
      };
    };

    React.useEffect(() => {
      sheetStore.updateCell({
        cellKey,
        pos,
        emptyValue: null,
        save,
        focus() {
          bref.current?.focus();
        },
        blur() {
          bref.current?.blur();
        },
      });
    });

    React.useEffect(() => {
      sheetStore.setSrcCellValue(cellKey, value);
    }, [value]);

    const [open, setOpen] = React.useState(false);
    const openDrawer = () => {
      setOpen(true);
    };

    return (
      <CellInternalBox store={sheetStore} cellKey={cellKey} pos={pos}>
        <button
          type="button"
          className={styles.button}
          ref={bref}
          onFocus={() => {
            sheetStore.activate(cellKey, false);
          }}
          onBlur={() => {
            sheetStore.blur(sheetStore.cellByKey(cellKey));
          }}
          onClick={() => {
            sheetStore.activate(cellKey, false);
            openDrawer();
          }}
          onKeyDown={(e) => sheetStore.onKeyDown(cellKey, e)}
        >
          <div className={styles.text}>{row.point}</div>
          <DownOutlined />
        </button>
        <Mc2PointSelectModal
          title="Выбрать из библиотеки"
          open={open}
          close={() => setOpen(false)}
          okText="Выбрать"
          settings={settings}
          multiple={false}
          success={(points) => {
            const pt = points[0];
            if (pt)
              sheetStore
                .cellTask(cellKey, async () => {
                  const fn = await save(pt);
                  fn();
                })
                .catch(onError);
          }}
          initialValue={{
            id: value,
            objectId: settings.objPointId!,
            attributeValues: [
              {
                attributeId: settings.pointAttrNameId!,
                values: [row.point],
              },
              {
                attributeId: settings.pointAttrDescriptionId!,
                values: [row.description ?? ""],
              },
            ],
          }}
        />
      </CellInternalBox>
    );
  },
);
